import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Mail,
  CheckCircle,
  Plus,
  Sparkles,
  Clock,
  Send,
  Trash2,
  Edit,
  Loader2,
  Calendar,
  Users,
  AlertCircle,
  BarChart3,
  Eye,
  MousePointerClick,
  TrendingUp,
  Wand2,
  Zap,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import RichTextEditor from "../RichTextEditor";

const API = process.env.REACT_APP_BACKEND_URL;

export const ProgramEmailsTab = ({ program, onOpenWizard, onRefresh }) => {
  const hasEmailSettings = !!program?.email_settings;
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showConfirmationEditor, setShowConfirmationEditor] = useState(false);
  const [savingConfirmation, setSavingConfirmation] = useState(false);
  const [confirmationForm, setConfirmationForm] = useState({
    confirmation_subject: "",
    confirmation_body: "",
  });
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiLoading, setAILoading] = useState(false);
  const [aiCleanupLoading, setAiCleanupLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [selectedReminderStats, setSelectedReminderStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [programStats, setProgramStats] = useState(null);
  const [sendMode, setSendMode] = useState("schedule"); // "now" or "schedule"

  // Form state
  const [formData, setFormData] = useState({
    subject: "",
    body_html: "",
    scheduled_date: "",
    scheduled_time: "09:00",
    recipient_filter: "all",
  });

  // AI form state
  const [aiPrompt, setAIPrompt] = useState("");
  const [aiTone, setAITone] = useState("friendly");

  useEffect(() => {
    if (program?.program_id) {
      seedAndFetch();
      fetchProgramStats();
    }
  }, [program?.program_id]);

  // Seed auto-reminders then fetch all reminders
  const seedAndFetch = async () => {
    try {
      const token = "cookie-session";
      await axios.post(
        `${API}/api/program-reminders/seed-auto-reminders/${program.program_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      // Seed is best-effort — don't block the UI
      console.log("Auto-reminder seed skipped:", e.response?.data?.message || e.message);
    }
    fetchReminders();
  };

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const token = "cookie-session";
      const response = await axios.get(
        `${API}/api/program-reminders?program_id=${program.program_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReminders(response.data);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramStats = async () => {
    try {
      const token = "cookie-session";
      const response = await axios.get(
        `${API}/api/email-tracking/program/${program.program_id}/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProgramStats(response.data);
    } catch (error) {
      console.error("Error fetching program stats:", error);
    }
  };

  const fetchReminderStats = async (reminderId) => {
    try {
      setStatsLoading(true);
      const token = "cookie-session";
      const response = await axios.get(
        `${API}/api/email-tracking/stats/${reminderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedReminderStats(response.data);
      setShowStatsDialog(true);
    } catch (error) {
      console.error("Error fetching reminder stats:", error);
      toast.error("Could not load email statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subject: "",
      body_html: "",
      scheduled_date: "",
      scheduled_time: "09:00",
      recipient_filter: "all",
    });
    setEditingReminder(null);
    setSendMode("schedule");
  };

  const handleCreateReminder = async () => {
    if (!formData.subject) {
      toast.error("Please enter a subject line");
      return;
    }
    if (sendMode === "schedule" && !formData.scheduled_date) {
      toast.error("Please select a send date");
      return;
    }

    try {
      setSaving(true);
      const token = "cookie-session";

      if (editingReminder) {
        await axios.put(
          `${API}/api/program-reminders/${editingReminder.reminder_id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Email updated successfully");
      } else {
        // For "send now", set the date to today and time to now
        const payload = { ...formData, program_id: program.program_id };
        if (sendMode === "now") {
          const now = new Date();
          payload.scheduled_date = now.toISOString().split("T")[0];
          payload.scheduled_time = now.toTimeString().slice(0, 5);
        }

        const response = await axios.post(
          `${API}/api/program-reminders`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // If "send now", immediately trigger sending
        if (sendMode === "now" && response.data?.reminder_id) {
          try {
            await axios.post(
              `${API}/api/program-reminders/${response.data.reminder_id}/send-now`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Email is being sent now!");
          } catch {
            toast.success("Email created — it will be sent shortly");
          }
        } else {
          toast.success("Email scheduled successfully");
        }
      }

      setShowCreateDialog(false);
      resetForm();
      fetchReminders();
      fetchProgramStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving email");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm("Are you sure you want to delete this email?")) return;
    try {
      const token = "cookie-session";
      await axios.delete(`${API}/api/program-reminders/${reminderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Email deleted");
      fetchReminders();
    } catch (error) {
      toast.error("Error deleting email");
    }
  };

  const handleSendNow = async (reminderId) => {
    if (!window.confirm("Send this email now?")) return;
    try {
      const token = "cookie-session";
      await axios.post(
        `${API}/api/program-reminders/${reminderId}/send-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email is being sent");
      fetchReminders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error sending email");
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please describe what you want the email to say");
      return;
    }
    try {
      setAILoading(true);
      const token = "cookie-session";
      const response = await axios.post(
        `${API}/api/program-reminders/ai/generate`,
        { program_id: program.program_id, prompt: aiPrompt, tone: aiTone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormData((prev) => ({
        ...prev,
        subject: response.data.subject,
        body_html: response.data.body_html,
      }));
      setShowAIDialog(false);
      setShowCreateDialog(true);
      toast.success(response.data.generated ? "AI generated your email!" : "Used template — AI was unavailable");
    } catch (error) {
      toast.error("Error generating content");
    } finally {
      setAILoading(false);
    }
  };

  const handleAiCleanup = async () => {
    if (!formData.body_html.trim()) {
      toast.error("Please write some content first");
      return;
    }
    try {
      setAiCleanupLoading(true);
      const token = "cookie-session";
      const response = await axios.post(
        `${API}/api/admin/ai/email-cleanup`,
        { content: formData.body_html, context: `Email for program: ${program?.name}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.cleaned_content) {
        setFormData((prev) => ({ ...prev, body_html: response.data.cleaned_content }));
        toast.success("Email cleaned up with AI!");
      }
    } catch (error) {
      toast.error("Failed to clean up with AI");
    } finally {
      setAiCleanupLoading(false);
    }
  };

  const handleEditReminder = (reminder) => {
    setFormData({
      subject: reminder.subject,
      body_html: reminder.body_html,
      scheduled_date: reminder.scheduled_date,
      scheduled_time: reminder.scheduled_time,
      recipient_filter: reminder.recipient_filter,
    });
    setEditingReminder(reminder);
    setSendMode("schedule");
    setShowCreateDialog(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: "bg-blue-100 text-blue-700",
      sent: "bg-green-100 text-green-700",
      failed: "bg-red-100 text-red-700",
      no_recipients: "bg-yellow-100 text-yellow-700",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-700"}`}>
        {status}
      </span>
    );
  };

  const getRecipientLabel = (filter) => {
    if (filter === "all") return "All Registrants";
    if (filter === "paid") return "Paid Only";
    if (filter === "pending") return "Pending Payment";
    if (filter?.startsWith("division:")) return filter.replace("division:", "");
    return filter;
  };

  // Separate sent vs scheduled reminders
  const sentEmails = reminders.filter(r => r.status === "sent");
  const scheduledEmails = reminders.filter(r => r.status === "scheduled");
  const failedEmails = reminders.filter(r => r.status === "failed" || r.status === "no_recipients");

  return (
    <div className="program-section program-communications" data-program-section="communications">
      <div className="program-section-heading"><div><h2>Communications</h2><p>Manage registration emails, scheduled messages and delivery activity.</p></div></div>

      {/* ═══ 1. EMAIL ANALYTICS — TOP ═══ */}
      <div className="program-data-panel program-email-analytics">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-slate-900">Email Analytics</h3>
        </div>
        <div className="program-section-metrics">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <Send className="w-4 h-4" />
              Sent
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {programStats?.total_emails_sent || 0}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <Eye className="w-4 h-4" />
              Opened
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {programStats?.total_opened || 0}
              <span className="text-sm font-normal text-green-600 ml-1">
                ({programStats?.overall_open_rate || 0}%)
              </span>
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <MousePointerClick className="w-4 h-4" />
              Clicked
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {programStats?.total_clicked || 0}
              <span className="text-sm font-normal text-blue-600 ml-1">
                ({programStats?.overall_click_rate || 0}%)
              </span>
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <Mail className="w-4 h-4" />
              Campaigns
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {programStats?.total_reminders_sent || 0}
            </p>
          </div>
        </div>
        {(!programStats || programStats.total_emails_sent === 0) && (
          <p className="text-sm text-slate-500 mt-3 text-center">
            No emails sent yet. Create an email below to get started.
          </p>
        )}
      </div>

      {/* ═══ 2. ACTIVE EMAILS (Confirmation + Automated) ═══ */}
      <div className="program-data-panel" data-testid="emails-tab">
        <h3 className="font-semibold text-slate-900 mb-1">Active Emails</h3>
        <p className="text-sm text-slate-500 mb-4">Emails automatically sent to registrants</p>

        {/* Registration Confirmation Email */}
        <div className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-slate-900">Registration Confirmation</h4>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Active</span>
                </div>
                <p className="text-sm text-slate-500 mt-0.5">
                  {hasEmailSettings
                    ? `Subject: ${program.email_settings.confirmation_subject}`
                    : "Default confirmation email sent on registration"
                  }
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => {
              setConfirmationForm({
                confirmation_subject: program.email_settings?.confirmation_subject || "Registration Confirmed - {program_name}",
                confirmation_body: program.email_settings?.confirmation_body || "",
              });
              setShowConfirmationEditor(true);
            }}>
              {hasEmailSettings ? "Edit" : "Configure"}
            </Button>
          </div>
        </div>

      </div>

      {/* ═══ 3. EMAILS (Sent + Scheduled + Create New) ═══ */}
      <div className="program-data-panel">
        <div className="program-data-toolbar">
          <div>
            <h3 className="font-semibold text-slate-900">Emails</h3>
            <p className="text-sm text-slate-500 mt-1">
              Send custom emails to program registrants
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setAIPrompt(""); setShowAIDialog(true); }}
              className="gap-2"
              data-testid="ai-generate-btn"
            >
              <Sparkles className="w-4 h-4" />
              Write with AI
            </Button>
            <Button
              size="sm"
              onClick={() => { resetForm(); setShowCreateDialog(true); }}
              className="gap-2"
              data-testid="create-reminder-btn"
            >
              <Plus className="w-4 h-4" />
              New Email
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : reminders.length === 0 ? (
          <div className="program-empty-state">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-slate-500">No emails created yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Create your first email to communicate with registrants
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Scheduled emails */}
            {scheduledEmails.length > 0 && (
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-2">Scheduled</p>
                <div className="space-y-2">
                  {scheduledEmails.map((reminder) => (
                    <ReminderRow
                      key={reminder.reminder_id}
                      reminder={reminder}
                      getStatusBadge={getStatusBadge}
                      getRecipientLabel={getRecipientLabel}
                      onEdit={handleEditReminder}
                      onSendNow={handleSendNow}
                      onDelete={handleDeleteReminder}
                      onViewStats={fetchReminderStats}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Sent emails */}
            {sentEmails.length > 0 && (
              <div>
                <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-2">Sent</p>
                <div className="space-y-2">
                  {sentEmails.map((reminder) => (
                    <ReminderRow
                      key={reminder.reminder_id}
                      reminder={reminder}
                      getStatusBadge={getStatusBadge}
                      getRecipientLabel={getRecipientLabel}
                      onEdit={handleEditReminder}
                      onSendNow={handleSendNow}
                      onDelete={handleDeleteReminder}
                      onViewStats={fetchReminderStats}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Failed emails */}
            {failedEmails.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-600 uppercase tracking-wide mb-2">Failed</p>
                <div className="space-y-2">
                  {failedEmails.map((reminder) => (
                    <ReminderRow
                      key={reminder.reminder_id}
                      reminder={reminder}
                      getStatusBadge={getStatusBadge}
                      getRecipientLabel={getRecipientLabel}
                      onEdit={handleEditReminder}
                      onSendNow={handleSendNow}
                      onDelete={handleDeleteReminder}
                      onViewStats={fetchReminderStats}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ DIALOGS ═══ */}

      {/* AI Generation Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Write with AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">
                What should this email say?
              </label>
              <Textarea
                placeholder="e.g., Remind parents about the upcoming equipment fitting day this Saturday. They should bring their player and arrive 15 minutes early."
                value={aiPrompt}
                onChange={(e) => setAIPrompt(e.target.value)}
                rows={4}
                className="mt-1"
                data-testid="ai-prompt-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Tone</label>
              <Select value={aiTone} onValueChange={setAITone}>
                <SelectTrigger className="mt-1" data-testid="ai-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="friendly">Friendly & Warm</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAIGenerate}
              disabled={aiLoading || !aiPrompt.trim()}
              className="gap-2"
              data-testid="ai-generate-submit"
            >
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Email Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {editingReminder ? "Edit Email" : "Create New Email"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Subject Line *</label>
              <Input
                placeholder="e.g., Important: Equipment Fitting This Saturday"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="mt-1"
                data-testid="reminder-subject-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700">Email Content *</label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button" variant="ghost" size="sm"
                    onClick={() => { setShowCreateDialog(false); setShowAIDialog(true); }}
                    className="text-xs gap-1 text-purple-600 hover:text-purple-700"
                    data-testid="ai-compose-btn"
                  >
                    <Sparkles className="w-3 h-3" /> Write with AI
                  </Button>
                  <Button
                    type="button" variant="ghost" size="sm"
                    onClick={handleAiCleanup}
                    disabled={aiCleanupLoading || !formData.body_html.trim()}
                    className="text-xs gap-1 text-purple-600 hover:text-purple-700"
                    data-testid="ai-cleanup-btn"
                  >
                    {aiCleanupLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                    Clean up
                  </Button>
                </div>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <RichTextEditor
                  value={formData.body_html}
                  onChange={(value) => setFormData({ ...formData, body_html: value })}
                  placeholder="Write your email content here..."
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Placeholders: {"{player_name}"}, {"{parent_name}"}, {"{program_name}"}, {"{start_date}"}, {"{location}"}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Send To</label>
              <Select
                value={formData.recipient_filter}
                onValueChange={(value) => setFormData({ ...formData, recipient_filter: value })}
              >
                <SelectTrigger className="mt-1" data-testid="reminder-recipient-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Registrants</SelectItem>
                  <SelectItem value="paid">Paid Only</SelectItem>
                  <SelectItem value="pending">Pending Payment Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Send Mode Toggle */}
            {!editingReminder && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">When to Send</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSendMode("now")}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      sendMode === "now"
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className={`w-4 h-4 ${sendMode === "now" ? "text-blue-600" : "text-slate-400"}`} />
                      <span className={`font-medium ${sendMode === "now" ? "text-blue-900" : "text-slate-700"}`}>
                        Send Now
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">Send immediately to all matching recipients</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendMode("schedule")}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      sendMode === "schedule"
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className={`w-4 h-4 ${sendMode === "schedule" ? "text-blue-600" : "text-slate-400"}`} />
                      <span className={`font-medium ${sendMode === "schedule" ? "text-blue-900" : "text-slate-700"}`}>
                        Schedule
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">Pick a date and time to send later</p>
                  </button>
                </div>
              </div>
            )}

            {/* Date/time fields only when scheduling */}
            {(sendMode === "schedule" || editingReminder) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Send Date *</label>
                  <Input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    className="mt-1"
                    data-testid="reminder-date-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Send Time *</label>
                  <Input
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    className="mt-1"
                    data-testid="reminder-time-input"
                  />
                </div>
              </div>
            )}

            {sendMode === "now" && !editingReminder && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">Heads up</p>
                  <p>This email will be sent immediately to all matching registrants. This action cannot be undone.</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateReminder}
              disabled={saving}
              className="gap-2"
              data-testid="reminder-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingReminder
                ? "Update Email"
                : sendMode === "now"
                ? "Send Now"
                : "Schedule Email"
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Email Editor Dialog */}
      <Dialog open={showConfirmationEditor} onOpenChange={setShowConfirmationEditor}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Registration Confirmation Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Subject Line</label>
              <Input
                value={confirmationForm.confirmation_subject}
                onChange={(e) => setConfirmationForm({ ...confirmationForm, confirmation_subject: e.target.value })}
                placeholder="Registration Confirmed - {program_name}"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Placeholders: {"{{program_name}}"}, {"{{player_name}}"}, {"{{parent_name}}"}, {"{{start_date}}"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Email Body</label>
              <div className="border border-slate-200 rounded-lg overflow-hidden mt-1">
                <RichTextEditor
                  value={confirmationForm.confirmation_body}
                  onChange={(value) => setConfirmationForm({ ...confirmationForm, confirmation_body: value })}
                  placeholder="Write your confirmation email content..."
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmationEditor(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                try {
                  setSavingConfirmation(true);
      const token = "cookie-session";
                  await axios.put(
                    `${API}/programs/${program.program_id}`,
                    {
                      email_settings: {
                        ...program.email_settings,
                        confirmation_subject: confirmationForm.confirmation_subject,
                        confirmation_body: confirmationForm.confirmation_body,
                      }
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  toast.success("Confirmation email updated!");
                  setShowConfirmationEditor(false);
                  onRefresh?.();
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to save email settings");
                } finally {
                  setSavingConfirmation(false);
                }
              }}
              disabled={savingConfirmation}
              className="gap-2"
            >
              {savingConfirmation && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Stats Dialog */}
      <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              Email Analytics
            </DialogTitle>
          </DialogHeader>
          {statsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : selectedReminderStats ? (
            <div className="space-y-6">
              <div className="program-section-metrics">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-slate-900">{selectedReminderStats.total_sent}</div>
                  <div className="text-sm text-slate-500">Sent</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{selectedReminderStats.open_rate}%</div>
                  <div className="text-sm text-slate-500">Open Rate</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">{selectedReminderStats.click_rate}%</div>
                  <div className="text-sm text-slate-500">Click Rate</div>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-700">{selectedReminderStats.total_clicks}</div>
                  <div className="text-sm text-slate-500">Total Clicks</div>
                </div>
              </div>
              {Object.keys(selectedReminderStats.click_breakdown || {}).length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Link Clicks</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedReminderStats.click_breakdown).map(([url, count]) => (
                      <div key={url} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-600 truncate max-w-md">{url}</span>
                        <span className="text-sm font-medium text-slate-900">{count} clicks</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <h4 className="font-medium text-slate-900 mb-2">
                  Recipients ({selectedReminderStats.tracking_records?.length || 0})
                </h4>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {selectedReminderStats.tracking_records?.map((record, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="text-slate-600">{record.email}</span>
                      <div className="flex items-center gap-3">
                        {record.opened ? (
                          <span className="flex items-center gap-1 text-green-600"><Eye className="w-4 h-4" /> Opened</span>
                        ) : (
                          <span className="text-slate-400">Not opened</span>
                        )}
                        {record.click_count > 0 && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <MousePointerClick className="w-4 h-4" /> {record.click_count}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Reminder/Email row component ─────────────────────────
const ReminderRow = ({ reminder, getStatusBadge, getRecipientLabel, onEdit, onSendNow, onDelete, onViewStats }) => (
  <div
    className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors"
    data-testid={`reminder-${reminder.reminder_id}`}
  >
    <div className="program-message-summary">
      <div className="program-message-subject">
        <h4 className="font-medium text-slate-900">{reminder.subject}</h4>
        {reminder.auto_type && <span className="text-violet-700">Suggested</span>}
      </div>
      <span>Audience: {getRecipientLabel(reminder.recipient_filter) || '—'}</span>
      <span>Scheduled: {(reminder.scheduled_datetime || reminder.scheduled_date) && !Number.isNaN(new Date(reminder.scheduled_datetime || reminder.scheduled_date).getTime())
        ? `${format(new Date(reminder.scheduled_datetime || reminder.scheduled_date), 'MMM d, yyyy')}${reminder.scheduled_time ? ` at ${reminder.scheduled_time}` : ''}` : '—'}</span>
      <span>{getStatusBadge(reminder.status)}</span>
      <span>Sent count: {reminder.sent_count ?? '—'}</span>
      <div className="flex items-center gap-1">
        {reminder.status === "sent" && reminder.tracking_enabled && (
          <Button variant="ghost" size="sm" onClick={() => onViewStats(reminder.reminder_id)}
            aria-label={`View stats for ${reminder.subject}`} className="text-purple-600 hover:text-purple-700" data-testid={`stats-${reminder.reminder_id}`}>
            <BarChart3 className="w-4 h-4" />
          </Button>
        )}
        {reminder.status === "scheduled" && (
          <>
            <Button variant="ghost" size="sm" onClick={() => onEdit(reminder)}
              aria-label={`Edit ${reminder.subject}`} data-testid={`edit-reminder-${reminder.reminder_id}`}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onSendNow(reminder.reminder_id)}
              aria-label={`Send ${reminder.subject} now`} className="text-blue-600 hover:text-blue-700" data-testid={`send-now-${reminder.reminder_id}`}>
              <Send className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(reminder.reminder_id)}
              aria-label={`Delete ${reminder.subject}`} className="text-red-600 hover:text-red-700" data-testid={`delete-reminder-${reminder.reminder_id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  </div>
);

export default ProgramEmailsTab;
